import {
  Dispatch,
  SetStateAction,
  useRef,
  useCallback,
  useEffect,
  useState
} from 'react';

/**
 * The `useSafeState` hook acts the same as React's `useState`,
 * where we can set the initial state, and it returns (1) the current state,
 * and (2) the state setter.
 *
 * But with `useSafeState`, it will only set the state when the component is mounted
 * in order to prevent memory leaks and avoid this warning:
 * "Warning: Can’t perform a React state update on an unmounted component."
 * When a component is unmounted, we don't want to set the state.
 *
 * This helps when we have async behavior where we call an async API call,
 * then based on the async response, we set the state. But if the component
 * unmounts before the API call resolves, we don't want to set the state.
 * */
// eslint-disable-next-line import/prefer-default-export
export const useSafeState = <S>(
  initialState: S | (() => S)
): [S, Dispatch<SetStateAction<S>>] => {
  const mountedRef = useRef(false);
  const [state, setState] = useState(initialState);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setSafeState = useCallback(
    (args) => (mountedRef.current ? setState(args) : () => {}),
    []
  );

  return [state, setSafeState];
};
